import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { interval, Observable, ReplaySubject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Store } from '@ngrx/store';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { distinctUntilChanged, filter, skip, startWith, takeUntil } from 'rxjs/operators';
import { animate, style, transition, trigger } from '@angular/animations';
import { get, isEmpty, isEqual } from 'lodash';

import { SubscriptionPlanEnum } from 'common/src/modules/subscription-activation/subscription-activation.emum';
import { selectCompanyProfile } from '../../../administration/store/selectors';
import { CompanyProfile } from '../../../administration/models/company-profile.model';
import { HrmService } from '../../../hrm/hrm.service';
import { AuthService } from 'common/src/auth/auth.service';
import { UserPermissionsService } from 'common/src/services/user-permissions.service';
import { SystemPreferencesPermissionsService } from 'common/src/services/system-preferences-permissions.service';
import { AppState } from '../../../store/state/app.state';
import { selectAccountingSettings, selectAppsCounters, selectUserShortcuts } from '../../../store/selectors/shared.selectors';
import { AccountingSettingsModel } from '../../../accounting/accounting-settings-module/models';
import { ActionButtonsService } from 'common/src/services/action-buttons.service';
import { AuthUserModel } from 'common/src/auth/auth-user.model';
import { LaunchpadApiService } from 'common/src/modules/start-screen-page/launchpad-api.service';
import { AuthUserPermissionsEnum } from 'common/src/auth/auth-user-permissions.enum';
import { SandboxHintService } from 'common/src/modules/quick-onboarding/sandbox-hint.service';
import { UpdatePaymentCardModalComponent } from 'common/src/modules/modals/modals-subscription-activation';
import { ExitSandboxModalComponent } from 'common/src/modules/quick-onboarding/components/exit-sandbox-modal/exit-sandbox-modal.component';
import { CompanyProfileModeEnum } from '../../../administration/enums';
import { ShortcutsConfig } from 'common/src/modules/modals/modals-control-center/shortcuts-modal/user-shortcuts.helper';
import { ShortcutsModalComponent } from 'common/src/modules/modals/modals-control-center';
import { UserShortcutEnum } from '../../../hrm/enums';
import { getNavItems, SideNavigationModulesChildItem, SideNavigationModulesItem } from './side-navigation-modules.config';
import { ShortcutConfigItemModel } from 'common/src/modules/modals/modals-control-center/shortcuts-modal/shortcuts-config-item.model';
import { PerfectScrollbarComponent } from 'ngx-perfect-scrollbar';
import { UserShortcutsModel } from '../../../hrm/models';

@Component({
  selector: 'rnpl-side-navigation',
  templateUrl: './side-navigation.component.html',
  styleUrls: ['./side-navigation.component.scss'],
  animations: [
    // Horizontal Slide (Left to Right)
    trigger('slideHorizontal', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)', opacity: 0 }),
        animate('200ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(-100%)', opacity: 0 }))
      ])
    ]),
  ]
})
export class SideNavigationComponent implements OnDestroy {

  public showShortcutsScrollIndicator = true;
  public showModulesScrollIndicator = true;
  public shortcutsScrollable = false;
  public modulesScrollable = false;
  public companyProfileMode = CompanyProfileModeEnum;
  public loggedUser: AuthUserModel;
  public authUserPermissions = AuthUserPermissionsEnum;
  public menuItems: SideNavigationModulesItem[] = [];
  public appsCounters: {[key: string]: number} = {} as {[key: string]: number};
  public companyProfile: CompanyProfile;
  public accountingSettings: AccountingSettingsModel;
  public userShortcuts: UserShortcutsModel;
  public activatedShortcuts: ShortcutConfigItemModel[] = [];
  public visibleShortcuts: ShortcutConfigItemModel[] = [];
  public hiddenShortcuts: ShortcutConfigItemModel[] = [];
  private componentBeforeNavigation = null;
  private currentUrl: string;
  public activeSubmenu: SideNavigationModulesItem = null;
  public _shortcutsContainer: ElementRef<HTMLDivElement>;
  private _shortcutsScrollRef: PerfectScrollbarComponent;
  private _modulesScrollRef: PerfectScrollbarComponent;
  private _opened: boolean;
  private shortcutsResizeObserver: any;
  private shortcutsFilterFrame: number;
  private shortcutsFilterTimeout: any;
  private scrollbarStateFrame: number;
  // public shortcutsMoreExpanded = false;

  @Input()
  set opened(value: boolean) {
    this._opened = value;
    this.scheduleFilterShortcuts();
  }

  get opened(): boolean {
    return this._opened;
  }

  @Output() toggleThemeClicked: EventEmitter<null> = new EventEmitter<null>();
  @Output() changeModeClicked: EventEmitter<null> = new EventEmitter<null>();
  @Output() profileMenuStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();



  @ViewChildren(MatMenuTrigger) public menuTriggers: QueryList<MatMenuTrigger>;

  @ViewChild('shortcutsScrollRef', { static: false })
  set shortcutsScrollRef(value: PerfectScrollbarComponent) {
    this._shortcutsScrollRef = value;
    this.scheduleScrollbarStateUpdate();
  }

  get shortcutsScrollRef(): PerfectScrollbarComponent {
    return this._shortcutsScrollRef;
  }

  @ViewChild('modulesScrollRef', { static: false })
  set modulesScrollRef(value: PerfectScrollbarComponent) {
    this._modulesScrollRef = value;
    this.scheduleScrollbarStateUpdate();
  }

  get modulesScrollRef(): PerfectScrollbarComponent {
    return this._modulesScrollRef;
  }

  @ViewChild('shortcutsContainer', { static: false })
  set shortcutsContainer(value: ElementRef<HTMLDivElement>) {
    if (value) {
      this._shortcutsContainer = value;
      this.observeShortcutsContainer();
      this.scheduleFilterShortcuts();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.scheduleFilterShortcuts();
  }

  public showSandboxHint$: Observable<boolean> = this.sandboxHintService.showSandboxHint$;
  public showOverduePaymentHint$: Observable<boolean> = this.sandboxHintService.showOverduePaymentHint$;
  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public hrmService: HrmService,
    public authService: AuthService,
    public sandboxHintService: SandboxHintService,
    public launchpadApiService: LaunchpadApiService,
    public userPermissionsService: UserPermissionsService,
    private systemPreferencesPermissionsService: SystemPreferencesPermissionsService,
    private actionButtonService: ActionButtonsService,
    private dialog: MatDialog,
    public store: Store<AppState>,
    private ngZone: NgZone,
  ) {
    this.currentUrl = this.router.url;

    // get app counters on module/application change
    this.router.events
      .pipe(
        filter((event: RouterEvent) => event instanceof NavigationEnd),
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        const nextUrl = event.urlAfterRedirects || event.url;
        const urlChanged = !!this.currentUrl && this.currentUrl !== nextUrl;
        this.currentUrl = nextUrl;

        if (urlChanged) {
          this.closeShortcutMenus();
          this.activeSubmenu = null;
        }
        let currentRoute = this.route;
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }
        if (this.componentBeforeNavigation !== currentRoute.component) {
          this.getAppCounters()
        }
        this.componentBeforeNavigation = currentRoute.component;
      });

    this.authService.loggedUser$
      .pipe(
        distinctUntilChanged(isEqual),
        takeUntil(this.destroy$)
      )
      .subscribe((user: AuthUserModel) => {
        this.loggedUser = user;
        this.setMenuItems();
      });

    this.store.select(selectAccountingSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((accountingSettings: AccountingSettingsModel) => this.accountingSettings = accountingSettings);

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => {
        this.companyProfile = companyProfile;
        this.setMenuItems();
        this.setShortcuts();
      });

    // Update counters every 5 minutes
    interval(300 * 10e2) // 300 * 10e2 = 5 minutes
      .pipe(
        startWith(0),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.getAppCounters());

    this.store.select(selectAppsCounters)
      .pipe(takeUntil(this.destroy$))
      .subscribe((counters) => {
        this.appsCounters = counters;
      });

    this.hrmService.getUserProfileAndBlueprintsV2(this.authService.getUser().id, false, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setMenuItems()
      });

    this.store.select(selectUserShortcuts)
      .pipe(takeUntil(this.destroy$))
      .subscribe((shortcuts: UserShortcutsModel) => {
        this.userShortcuts = shortcuts;
        this.setShortcuts();
      });
  }

  public setShortcuts(): void {
    if (!this.userShortcuts || !this.loggedUser || !this.companyProfile || isEmpty(this.companyProfile)) { return; }

    const wholesaleEnabled = this.systemPreferencesPermissionsService.wholesaleEnabled();
    const servicesEnabled = this.systemPreferencesPermissionsService.servicesEnabled();
    const canViewWholesale = this.userPermissionsService.canViewWholesale();
    const canViewServices = this.userPermissionsService.canViewServices();
    const canViewEcommerce = this.userPermissionsService.canViewEcommerce();

    const config = ShortcutsConfig(
      this.userPermissionsService.canManageProducts(),
      this.userPermissionsService.canManagePartners(),
      this.userPermissionsService.canManageWarehouse(),
      this.userPermissionsService.canManageAccounting(),
      this.userPermissionsService.canManageWholesale(),
      this.userPermissionsService.canManageServices(),
      this.companyProfile.productOptions.goodsEnabled,
      this.companyProfile.productOptions.servicesEnabled,
      this.companyProfile.productOptions.digitalEnabled,
      this.companyProfile.productOptions.bundlesEnabled,
      this.systemPreferencesPermissionsService.corporatePartnersEnabled(),
      this.systemPreferencesPermissionsService.privatePartnersEnabled(),
      (canViewWholesale || canViewServices) && (wholesaleEnabled || servicesEnabled || this.isFreePlan || this.isAccountingPlan || this.companyProfile.subscriptionManagement.subscriptionPermissions.SALES_PRICES),
      (canViewWholesale || canViewEcommerce || canViewServices),
    );
    this.activatedShortcuts = [...this.userShortcuts.activated]
      .sort((a, b) => Number(b.pinned) - Number(a.pinned))
      .map(itm => ({
        ...config[itm.title],
        pinned: itm.pinned
      }));
    this.scheduleFilterShortcuts();
  }

  public filterShortcuts(): void {
    if (!this.activatedShortcuts || !this.activatedShortcuts.length || !this._shortcutsContainer) { return; }

    const containerHeight = this._shortcutsContainer.nativeElement.clientHeight;
    const shortcutHeight = 42; // 40px (includes 2px of bottom margin)

    let visibleCount = Math.floor(containerHeight / shortcutHeight);

    // Reserve one slot for "Show More" if not everything fits
    if (this.activatedShortcuts.length > visibleCount) {
      visibleCount = Math.max(0, visibleCount - 1);
    }

    this.visibleShortcuts = this.activatedShortcuts.slice(0, visibleCount);
    this.hiddenShortcuts = this.activatedShortcuts.slice(visibleCount);
    this.scheduleScrollbarStateUpdate();
  }

  private closeShortcutMenus(): void {
    if (!this.menuTriggers) { return; }

    this.menuTriggers.forEach((menuTrigger: MatMenuTrigger) => menuTrigger.closeMenu());
  }

  public onShortcutsScrollUp(): void {
    this.showShortcutsScrollIndicator = true;
    this.scheduleScrollbarStateUpdate();
  }

  public onShortcutsReachEnd(event): void {
    this.showShortcutsScrollIndicator = !event.target.scrollTop;
    this.scheduleScrollbarStateUpdate();
  }

  public onModulesScrollUp(): void {
    this.showModulesScrollIndicator = true;
    this.scheduleScrollbarStateUpdate();
  }

  public onModulesReachEnd(event): void {
    this.showModulesScrollIndicator = !event.target.scrollTop;
    this.scheduleScrollbarStateUpdate();
  }

  private scheduleFilterShortcuts(): void {
    if (this.shortcutsFilterFrame) {
      cancelAnimationFrame(this.shortcutsFilterFrame);
    }
    if (this.shortcutsFilterTimeout) {
      clearTimeout(this.shortcutsFilterTimeout);
    }

    this.shortcutsFilterFrame = requestAnimationFrame(() => {
      this.ngZone.run(() => {
        this.shortcutsFilterFrame = null;
        this.filterShortcuts();
        this.scheduleScrollbarStateUpdate();
      });
    });
    this.shortcutsFilterTimeout = setTimeout(() => {
      this.ngZone.run(() => {
        this.shortcutsFilterTimeout = null;
        this.filterShortcuts();
        this.scheduleScrollbarStateUpdate();
      });
    }, 250);
  }

  private scheduleScrollbarStateUpdate(): void {
    if (this.scrollbarStateFrame) {
      cancelAnimationFrame(this.scrollbarStateFrame);
    }

    this.scrollbarStateFrame = requestAnimationFrame(() => {
      this.ngZone.run(() => {
        this.scrollbarStateFrame = null;
        this.shortcutsScrollable = !!this.shortcutsScrollRef &&
          this.shortcutsScrollRef.directiveRef.scrollable('y');
        this.modulesScrollable = !!this.modulesScrollRef &&
          this.modulesScrollRef.directiveRef.scrollable('y');
      });
    });
  }

  private observeShortcutsContainer(): void {
    if (this.shortcutsResizeObserver) {
      this.shortcutsResizeObserver.disconnect();
    }

    if (!(window as any).ResizeObserver) { return; }

    this.shortcutsResizeObserver = new (window as any).ResizeObserver(() => {
      this.scheduleFilterShortcuts();
    });
    this.shortcutsResizeObserver.observe(this._shortcutsContainer.nativeElement);
  }

  private setMenuItems(): void {
    if (!this.companyProfile || !this.companyProfile.subscriptionManagement || !this.loggedUser) {
      return;
    }
    this.menuItems = getNavItems(
      this.userPermissionsService,
      get(this.accountingSettings, 'reportingSettings.currentReportingType') === 'SIMPLIFIED',
      this.companyProfile,
    )
      .filter(i => !i.hasOwnProperty('show') || i.show)
      .map(item => ({
        ...item,
        childrenGroups: item.childrenGroups
          .map(childGroup => ({
            ...childGroup,
            children: childGroup.children.filter(childItem => !childItem.hasOwnProperty('show') || childItem.show)
          }))
          .filter(childGroup => childGroup.children.length)
      }));

    if (this.activeSubmenu) {
      this.activeSubmenu = this.menuItems.find(itm => itm.title === this.activeSubmenu.title);
    }
  }

  public getAppCounters(): void {
    this.launchpadApiService.getAppsCounters()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  public actionHandler(action: string, event: Event) {
    event.stopPropagation();
    this.actionButtonService.callAction(action);
  }

  public toggleTheme(): void {
    this.toggleThemeClicked.emit();
  }

  public profileMenuToggle(isOpen): void {
    this.profileMenuStateChanged.emit(isOpen);
  }

  public changeMenuMode(): void {
    this.changeModeClicked.emit();
  }

  public showSandboxHint(): void {
    this.sandboxHintService.setShowHint = true;
  }

  public showOverduePaymentHint(): void {
    this.sandboxHintService.setOverduePaymentShowHint = true;
  }

  public updateOverduePaymentCard(e): void {
    e.stopPropagation();
    this.sandboxHintService.setOverduePaymentShowHint = false;
    this.dialog.open(UpdatePaymentCardModalComponent, {
      maxWidth: '480px',
      panelClass: 'mat-dialog-container-min-width-auto',
      disableClose: true
    });
  }

  public exitSandbox(e): void {
    e.stopPropagation();
    this.sandboxHintService.setShowHint = false;
    this.dialog.open(ExitSandboxModalComponent, {
      data: {
        companyProfile: this.companyProfile
      },
      disableClose: true
    });
  }

  public toggleSandboxHint(e, value: boolean): void {
    e.stopPropagation();
    this.sandboxHintService.setShowHint = value;
  }

  public toggleOverduePaymentHint(e, value: boolean): void {
    e.stopPropagation();
    this.sandboxHintService.setOverduePaymentShowHint = value;
  }

  public openShortcutsSettingsModal(): void {
    this.dialog.open(ShortcutsModalComponent, {
      minWidth: '680px',
      maxWidth: '680px',
      disableClose: true
    });
  }

  public contextMenuActionsHandler(action: string): void {
    if (this[action]) {
      this[action]();
    } else {
      this.actionButtonService.callAction(action);
    }
  }

  public removeShortcut(shortcutKey: UserShortcutEnum): void {
    const shortcuts: UserShortcutEnum[] = this.activatedShortcuts
      .map(i => i.key)
      .filter(key => key !== shortcutKey);
    this.hrmService.updateUserModulesShortcuts(this.loggedUser.id, shortcuts)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  public pinShortcut(shortcutKey: UserShortcutEnum): void {
    this.hrmService.pinUnpinShortcut(this.loggedUser.id, shortcutKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  public toggleSubMenu(subMenuItem: SideNavigationModulesItem): void {
    if (!this.activeSubmenu || this.activeSubmenu.title !== subMenuItem.title) {
      this.activeSubmenu = subMenuItem;
      if (this.modulesScrollRef) {
        setTimeout(() => {
          this.modulesScrollRef.directiveRef.update();
          this.scheduleScrollbarStateUpdate();
        });
      }
    } else {
      this.activeSubmenu = null;
    }
  }

  public selectSubmenuItem(childItem: SideNavigationModulesChildItem): void {
    if (childItem && childItem.shortcutKey) {
      this.trackShortcutKeyNavigation(childItem.shortcutKey);
    }
    if (childItem && this.router.isActive(childItem.routerLink, false)) { return; }
    this.activeSubmenu = null;
  }

  public shortcutNotActivated(shortcutKey: UserShortcutEnum): boolean {
    return !this.activatedShortcuts.some((activated: ShortcutConfigItemModel) => activated.key === shortcutKey);
  }

  public addShortcut(shortcutKey: UserShortcutEnum): void {
    const shortcuts: UserShortcutEnum[] = this.activatedShortcuts
      .map(i => i.key);
    shortcuts.push(shortcutKey);
    this.hrmService.updateUserModulesShortcuts(this.loggedUser.id, shortcuts)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  public trackShortcutNavigation(shortcut: ShortcutConfigItemModel): void {
    if (shortcut.routerLink && shortcut.key) {
      this.trackShortcutKeyNavigation(shortcut.key);
    }
    if (shortcut.subitems && shortcut.subitems.length === 1) {
      this.trackShortcutKeyNavigation(shortcut.key);
    }
  }

  public trackShortcutKeyNavigation(shortcut: UserShortcutEnum): void {
    this.hrmService.increaseShortcutCounter(this.loggedUser.id, shortcut)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  ngOnDestroy() {
    if (this.shortcutsFilterFrame) {
      cancelAnimationFrame(this.shortcutsFilterFrame);
    }
    if (this.shortcutsFilterTimeout) {
      clearTimeout(this.shortcutsFilterTimeout);
    }
    if (this.scrollbarStateFrame) {
      cancelAnimationFrame(this.scrollbarStateFrame);
    }
    if (this.shortcutsResizeObserver) {
      this.shortcutsResizeObserver.disconnect();
    }
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get isFreePlan(): boolean {
    return this.companyProfile &&
      this.companyProfile.subscriptionManagement &&
      this.companyProfile.subscriptionManagement.subscriptionPlan === SubscriptionPlanEnum.FREE;
  }

  get isAccountingPlan(): boolean {
    return this.companyProfile &&
      this.companyProfile.subscriptionManagement &&
      (this.companyProfile.subscriptionManagement.subscriptionPlan === SubscriptionPlanEnum.ACCOUNTING_MONTHLY ||
        this.companyProfile.subscriptionManagement.subscriptionPlan === SubscriptionPlanEnum.ACCOUNTING_ANNUALLY);
  }

}
